import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import API from './api';
import OnboardingOverlay from './OnboardingOverlay';

const ENGINE_PATH_RE = /^\/engine\/visinvoke-[a-z0-9-]+-engine\.html(\?.*)?$/;
const ENGINE_VALUE_RE = /^[a-z0-9-]+$/;

export default function EngineWrapper() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const iframeRef = useRef(null);

  const slug = location.state?.slug || '';

  const [engineSrc,   setEngineSrc]   = useState('');
  const [engineError, setEngineError] = useState('');
  const [generating,  setGenerating]  = useState(false);
  const [errorMsg,    setErrorMsg]    = useState('');

  useEffect(() => {
    try { sessionStorage.removeItem('active_slug'); sessionStorage.removeItem('vi_navigating'); } catch(e) {}
  }, []);

  useEffect(() => {
    if (!slug) { navigate('/dashboard', { replace: true }); }
  }, [slug, navigate]);

  useEffect(() => {
    if (!slug) return;
    API.get(`/v1/generators/${slug}`)
      .then(res => {
        const engine = res.data?.generator?.engine;
        if (engine === undefined || engine === null || engine === '') {
          throw new Error(`Engine missing for slug: ${slug}`);
        }
        if (typeof engine !== 'string' || !ENGINE_VALUE_RE.test(engine)) {
          throw new Error(`Invalid engine value: ${engine}`);
        }
        const engineSrc = `/engine/visinvoke-${engine}-engine.html?slug=${encodeURIComponent(slug)}&v=${Math.floor(Date.now()/86400000)}&b=14`;
        if (!ENGINE_PATH_RE.test(engineSrc)) {
          throw new Error(`Invalid engine path constructed: ${engineSrc}`);
        }
        console.debug('ENGINE LOAD', { slug, engine, engineSrc });
        setEngineSrc(engineSrc);
      })
      .catch(err => {
        const msg = err.message || 'Failed to load generator. Please try again.';
        console.error('EngineWrapper:', msg);
        setEngineError(msg);
      });
  }, [slug]);

  const handleMessage = useCallback(async (event) => {
    if (!event.data || typeof event.data !== 'object') return;

    // --- Image generation (existing, unchanged) ---
    if (event.data.type === 'VISINVOKE_GENERATE_REQUEST') {
      const { slug: engineSlug, state, image_count, quality, credits_required } = event.data.payload || {};
      if (!engineSlug || !state) {
        setErrorMsg('Engine sent incomplete state. Please try again.');
        return;
      }
      await handleGenerate(engineSlug, state, image_count || 1, quality || 'draft', credits_required || 1);
      return;
    }

    // --- DM tool state: LOAD (parallel handler, transport only) ---
    if (event.data.type === 'VISINVOKE_LOAD_REQUEST') {
      await handleStateLoad(event.source, event.data.payload || {});
      return;
    }

    // --- DM tool state: SAVE (parallel handler, transport only) ---
    if (event.data.type === 'VISINVOKE_SAVE_REQUEST') {
      await handleStateSave(event.source, event.data.payload || {});
      return;
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleMessage]);

  function markGenerationSuccess(engineSlug) {
    try {
      localStorage.setItem('hasGeneratedImage', 'true');
      // Initialise step progression ONLY if not already set
      if (!localStorage.getItem('onboarding_local_step')) {
        localStorage.setItem('onboarding_local_step', '1');
      }
    } catch (e) { console.warn('localStorage write failed:', e); }
  }

  // ===========================================================================
  // DM tool state bridge (transport only)
  //
  // EngineWrapper is a bridge. It does NOT understand tiers, tool selections,
  // memberships, quotas, or any Combat PRO rules -- all of that lives in the
  // backend guard on /v1/dm-tools/<slug>/state. These helpers only: validate
  // the message shape, call the endpoint (auth via the HttpOnly cookie on the
  // shared API instance), and post a structured response back to the iframe.
  //
  // They never throw and never crash the iframe: every failure -- including a
  // 403 from the backend guard -- is returned as { success: false, error }.
  // No retries, no caching, no localStorage fallback.
  // ===========================================================================

  function postToEngine(target, message) {
    try {
      if (target && typeof target.postMessage === 'function') {
        target.postMessage(message, '*');
      }
    } catch (e) {
      console.warn('postToEngine failed:', e);
    }
  }

  async function handleStateLoad(source, payload) {
    const engineSlug = payload && payload.slug;
    if (!engineSlug) {
      postToEngine(source, {
        type: 'VISINVOKE_LOAD_RESPONSE',
        payload: { success: false, error: 'Missing slug in load request.' },
      });
      return;
    }
    try {
      const res = await API.get(`/v1/dm-tools/${encodeURIComponent(engineSlug)}/state`);
      const data = res.data || {};
      postToEngine(source, {
        type: 'VISINVOKE_LOAD_RESPONSE',
        payload: {
          success:    true,
          slug:       engineSlug,
          state_json: data.state_json || {},
          updated_at: data.updated_at || null,
        },
      });
    } catch (err) {
      const status = err.response?.status;
      const error  = err.response?.data?.error
        || (status === 401 ? 'Your session has expired. Please sign in again.'
            : status === 403 ? 'You do not have access to this tool.'
            : 'Failed to load saved state.');
      postToEngine(source, {
        type: 'VISINVOKE_LOAD_RESPONSE',
        payload: { success: false, slug: engineSlug, error },
      });
    }
  }

  async function handleStateSave(source, payload) {
    const engineSlug = payload && payload.slug;
    const stateJson  = payload && payload.state_json;
    if (!engineSlug) {
      postToEngine(source, {
        type: 'VISINVOKE_SAVE_RESPONSE',
        payload: { success: false, error: 'Missing slug in save request.' },
      });
      return;
    }
    if (stateJson === undefined || stateJson === null || typeof stateJson !== 'object' || Array.isArray(stateJson)) {
      postToEngine(source, {
        type: 'VISINVOKE_SAVE_RESPONSE',
        payload: { success: false, slug: engineSlug, error: 'state_json must be an object.' },
      });
      return;
    }
    try {
      const res = await API.post(`/v1/dm-tools/${encodeURIComponent(engineSlug)}/state`, {
        state_json: stateJson,
      });
      const data = res.data || {};
      postToEngine(source, {
        type: 'VISINVOKE_SAVE_RESPONSE',
        payload: {
          success:    true,
          slug:       engineSlug,
          updated_at: data.updated_at || null,
        },
      });
    } catch (err) {
      const status = err.response?.status;
      const error  = err.response?.data?.error
        || (status === 401 ? 'Your session has expired. Please sign in again.'
            : status === 403 ? 'You do not have access to this tool.'
            : 'Failed to save state.');
      postToEngine(source, {
        type: 'VISINVOKE_SAVE_RESPONSE',
        payload: { success: false, slug: engineSlug, error },
      });
    }
  }

  async function handleGenerate(engineSlug, state, count, quality, creditsRequired) {
    if (generating) return;
    setGenerating(true);
    setErrorMsg('');
    try {
      const res = await API.post('/api/generate', {
        slug:             engineSlug,
        state:            state,
        image_count:      count,
        quality:          quality,
        credits_required: creditsRequired,
      });
      const data = res.data;
      if (data && data.status === 'COMPLETE' && data.image_url) {
        markGenerationSuccess(engineSlug);
        try {
          localStorage.removeItem('generation_job');
          localStorage.setItem('generation_result', JSON.stringify({
            status:      'COMPLETE',
            image_url:   data.image_url,
            image_urls:  data.image_urls || [data.image_url],
            seed:        data.seed || null,
            seed_base:   data.seed_base || null,
            slug:        engineSlug,
            image_count: count,
          }));
        } catch (e) { console.warn('localStorage write failed:', e); }
        navigate('/generating');
        return;
      }
      if (data && data.job_id) {
        markGenerationSuccess(engineSlug);
        try {
          localStorage.removeItem('generation_result');
          localStorage.setItem('generation_job', JSON.stringify({
            job_id: data.job_id,
            slug:   engineSlug,
          }));
        } catch (e) { console.warn('localStorage write failed:', e); }
        navigate('/generating');
        return;
      }
      setErrorMsg('Generation failed to start. Please try again.');
    } catch (err) {
      const status = err.response?.status;
      if (status === 401) { navigate('/login'); return; }
      if (status === 402) { setErrorMsg('Insufficient credits — please top up or upgrade your plan.'); }
      else if (status === 409) { setErrorMsg('A generation is already in progress. Please wait.'); }
      else if (status === 403) { setErrorMsg('Access denied. Your account may be restricted.'); }
      else { setErrorMsg('Generation failed. Please try again.'); }
    } finally {
      setGenerating(false);
    }
  }

  if (engineError) {
    return (
      <div style={{ ...s.page, alignItems: 'center', justifyContent: 'center' }}>
        <div style={s.errorBadge}>{engineError}</div>
      </div>
    );
  }

  if (!engineSrc) {
    return (
      <div style={{ ...s.page, alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: '#a78bfa', fontSize: 14 }}>Loading engine...</div>
      </div>
    );
  }

  return (
    <div style={s.page}>
      <OnboardingOverlay />
      {errorMsg && (
        <div style={s.errorBar}>
          {errorMsg}
          <button style={s.errorDismiss} onClick={() => setErrorMsg('')} type="button">✕</button>
        </div>
      )}
      <iframe
        ref={iframeRef}
        src={engineSrc}
        style={s.iframe}
        title="VISINVOKE Engine"
        allow="clipboard-write"
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals allow-top-navigation-by-user-activation"
      />
    </div>
  );
}

const s = {
  page: {
    display:       'flex',
    flexDirection: 'column',
    height:        '100vh',
    width:         '100%',
    overflow:      'hidden',
    background:    '#0F2044',
    fontFamily:    "'Segoe UI', sans-serif",
  },
  errorBar: {
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'space-between',
    flexShrink:     0,
    padding:        '10px 20px',
    background:     'rgba(220,38,38,0.15)',
    borderBottom:   '1px solid rgba(220,38,38,0.35)',
    fontSize:       13,
    fontWeight:     600,
    color:          '#fca5a5',
    gap:            12,
  },
  errorDismiss: {
    background:   'transparent',
    border:       'none',
    color:        '#fca5a5',
    cursor:       'pointer',
    fontSize:     14,
    padding:      '0 4px',
    flexShrink:   0,
  },
  errorBadge: {
    background:   'rgba(220,38,38,0.15)',
    border:       '1px solid rgba(220,38,38,0.35)',
    borderRadius: 8,
    padding:      '10px 20px',
    fontSize:     13,
    fontWeight:   600,
    color:        '#fca5a5',
    maxWidth:     420,
    lineHeight:   1.4,
  },
  iframe: {
    flex:      1,
    width:     '100%',
    border:    'none',
    display:   'block',
    minHeight: 0,
  },
};
